import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, CheckCircle2, XCircle } from "lucide-react";
import { API } from "../App";
import { buildReviewUrl } from "../lib/reviewUrl";

const InvoiceViewPage = () => {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/invoices/${invoiceId}`);
        setInv(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Invoice not found");
      }
    })();
  }, [invoiceId]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Invoice not found</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-saturn-orange animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-slate-900 text-lg font-semibold hidden md:block">
            Invoice <span className="font-mono">{inv.invoice_number}</span>
          </h1>
          <a
            href={`${API}/invoices/${inv.invoice_id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-saturn-orange hover:bg-saturn-orange/80 text-white px-5 py-2 rounded-lg font-medium ml-auto"
            data-testid="download-invoice-pdf"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">SATURN STYLING</h1>
              <p className="text-sm opacity-90">Premium Automotive Tinting & Styling</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold">TAX INVOICE</p>
              <p className="text-xs opacity-80">www.saturnstyling.ie</p>
            </div>
          </div>

          {/* Meta */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b">
            <div>
              <p className="font-semibold text-slate-900">SaturnStyling</p>
              <p className="text-sm text-slate-600 mt-1">Unit 4 Platin Industrial Estate</p>
              <p className="text-sm text-slate-600">Drogheda Road, Duleek, Drogheda</p>
              <p className="text-sm text-slate-600">Co. Meath, A92 E8WK, Ireland</p>
              <p className="text-sm text-slate-700 mt-2">
                <strong>VAT No:</strong> 1071602N
              </p>
              <p className="text-sm text-slate-700">Tel: 0858237373</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Invoice #</p>
              <p className="font-mono font-bold text-slate-900 text-lg">{inv.invoice_number}</p>
              <p className="text-sm text-slate-500 mt-2">Issued</p>
              <p className="text-slate-900">{inv.issued_date_display}</p>
            </div>
          </div>

          {/* Bill To */}
          <div className="p-6 bg-slate-50 border-b">
            <p className="text-xs font-bold text-slate-500 tracking-wider mb-2">BILL TO</p>
            <p className="font-semibold text-slate-900">{inv.customer_name}</p>
            <p className="text-sm text-slate-600">{inv.customer_email}</p>
            <p className="text-sm text-slate-600">{inv.customer_phone}</p>
            {inv.vehicle_desc && (
              <p className="text-sm text-slate-700 mt-2">
                {inv.vehicle_desc}
                {inv.vehicle_reg && <span className="ml-2 font-mono">· {inv.vehicle_reg}</span>}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-1">Booking Ref: {inv.booking_id}</p>
          </div>

          {/* Line Items */}
          <div className="p-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left p-3">DESCRIPTION</th>
                  <th className="text-right p-3">NET</th>
                  <th className="text-right p-3">VAT ({(inv.vat_rate * 100).toFixed(1)}%)</th>
                  <th className="text-right p-3">GROSS</th>
                </tr>
              </thead>
              <tbody>
                {inv.line_items?.map((li, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-3 text-slate-800">{li.description}</td>
                    <td className="p-3 text-right text-slate-700">€{li.net_amount.toFixed(2)}</td>
                    <td className="p-3 text-right text-slate-700">€{li.vat_amount.toFixed(2)}</td>
                    <td className="p-3 text-right font-semibold text-slate-900">€{li.gross_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mt-6">
              <div className="w-full md:w-1/2 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal (Net)</span>
                  <span>€{inv.subtotal_net.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>VAT @ {(inv.vat_rate * 100).toFixed(1)}%</span>
                  <span>€{inv.vat_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between bg-slate-900 text-white p-3 rounded font-bold">
                  <span>TOTAL (VAT incl.)</span>
                  <span>€{inv.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment summary */}
            <div className="mt-8 bg-green-50 border border-green-200 rounded p-4">
              <p className="font-semibold text-slate-900 mb-2">Payment Summary</p>
              <div className="flex justify-between text-sm text-slate-700">
                <span>Deposit paid</span>
                <span>€{inv.deposit_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-700">
                <span>Balance paid</span>
                <span>€{inv.balance_amount.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 text-green-700 font-bold">
                <CheckCircle2 className="w-5 h-5" />
                <span>PAID IN FULL</span>
              </div>
            </div>

            {/* Review CTA */}
            <div className="mt-6 text-center bg-gradient-to-r from-orange-50 to-amber-50 p-5 rounded border border-orange-200">
              <p className="text-slate-900 font-semibold mb-1">Enjoying the finish?</p>
              <p className="text-slate-600 text-sm mb-3">Your review helps other drivers find us.</p>
              <a
                href={buildReviewUrl(inv.booking_id, "invoice_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-amber-500 hover:bg-amber-600 text-slate-900 px-6 py-2 rounded-lg font-bold"
                data-testid="invoice-review-cta-btn"
              >
                ⭐ Leave a review
              </a>
            </div>
          </div>

          <div className="text-center text-xs text-slate-400 p-4 border-t">
            Thank you for choosing Saturn Styling.
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceViewPage;
